import { Urn } from "@jescrich/urn";
import type { IdTokenClaims } from "oidc-client-ts";

type ProjectRoles = {
  admin?: boolean;
  mtra?: boolean;
  patient?: boolean;
};

export const isAdmin = (claims?: IdTokenClaims): boolean => {
  const roles = claims?.["urn:zitadel:iam:org:project:roles"] as
    ProjectRoles | undefined;
  return !!roles?.admin;
};

export const isMtra = (claims?: IdTokenClaims): boolean => {
  const roles = claims?.["urn:zitadel:iam:org:project:roles"] as
    ProjectRoles | undefined;
  return !!roles?.mtra;
};

/**
 * Extracts all project-related claim keys from the user's profile.
 * @param profile The user's profile object (e.g., from auth.user?.profile)
 * @returns An array of claim keys related to projects.
 */
export function getUserProjectClaimKeys(
  profile: Record<string, unknown> | undefined,
): string[] {
  if (!profile) return [];
  return Object.keys(profile).filter((key) =>
    /^urn:zitadel:iam:org:project:\d+/.test(key),
  );
}

/**
 * Get the resource Ids of the user
 * @param claimKeys Array of project-related claim keys (URNs)
 * @returns The resource Ids the user has access to
 */
export function getAccessibleResourceIdsFromClaims(
  claimKeys: string[],
): (string | null)[] {
  const accessibleResourceIds = claimKeys
    .map((claim) => {
      let parsed;
      try {
        parsed = Urn.parse(claim);
      } catch (e) {
        console.error("Invalid URN in user claims:", claim, e);
        return null;
      }
      const resourceId = Object.entries(parsed.attributes || {}).find(
        ([key, value]) => /^\d+$/.test(key) && value,
      );
      return resourceId ? resourceId[0] : null;
    })
    .filter(Boolean);

  if (!accessibleResourceIds?.length) {
    console.warn("No accessible resource IDs found from claims.");
    return [];
  }
  return accessibleResourceIds;
}
